import styled from 'styled-components';
import { CustomImage, CustomPopover } from '../index';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import iconBilgiYellow from '@src/assets/icons/icon-bilgi-yellow.svg';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';

import { Text } from '@src/components';
import { getPendingNotifications } from '@src/store/slice/notificationSlice';

const NotificationContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 421px;
  height: 265px;
  font-family: UbuntuMedium, sans-serif;

  @media (max-width: 768px) {
    width: 326px;
    height: 283px;
  }

  .notification-header {
    display: flex;
    justify-content: space-between;
    padding: 15px 24px 11px 24px;
    border-bottom: solid 1px #d5d8de;

    span {
      font-family: UbuntuMedium, sans-serif;
      font-size: 16px;
      line-height: 24px;
      color: #3f4957;
    }

    .pagination-btns {
      display: flex;

      .swiper-page {
        margin: 0 10px;

        span {
          font-family: UbuntuRegular, sans-serif;
        }
      }

      img {
        cursor: pointer;
      }

      .swiper-button-disabled {
        cursor: unset;
      }
    }
  }

  .notification-popover-slider {
    padding-right: 1px;

    .swiper-wrapper {
      .notification-body {
        width: 100% !important;
        padding: 5px 18px;
        display: flex;
        flex-direction: column;
        row-gap: 4px;
        overflow-y: auto;

        @media (max-width: 768px) {
          row-gap: 10px;
        }

        &::-webkit-scrollbar {
          width: 0px;
          height: 0px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: transparent;
        }

        @media (min-width: 992px) {
          padding: 15px 19px 32px 24px;
        }

        @media (max-width: 768px) {
          padding: 18px 13px 0 13px !important;

          p {
            font-size: 12px !important;
            line-height: normal !important;
            margin-bottom: 10px !important;
          }
        }

        p {
          font-family: UbuntuLight, sans-serif;
          font-size: 12px;
          color: #1d2228;
          position: relative;
          padding-left: 15px;
          margin-bottom: 0;

          span {
            text-decoration: none;
            cursor: pointer;
            margin-left: 5px;
          }
        }

        .nav-link {
          border-radius: 8px;
          padding: 12px 10px;
          margin-bottom: 5px;
          margin-top: 5px;
          padding-bottom: 0;
          .border {
            border-top: solid 1px #e2e8f0 !important;
            margin-top: 12px;
          }
        }

        .nav-link:hover {
          background-color: #fff4e6;
          .border {
            border-top: none !important;
          }
        }
      }
    }
  }
`;

const NotificationPopover = ({
  children,
  isNotificationPopoverVisible,
  setIsNotificationPopoverVisible,
  notificationList,
}) => {
  const [notificationPopoverContent, setNotificationPopoverContent] = useState(false);
  const [notificationPopoverSwiper, setNotificationPopoverSwiper] = useState(false);
  const dispatch = useDispatch();
  const isStale = useSelector((state) => state?.notifications?.isStale);

  // The notification texts are produced by the backend. When the language is switched the list is
  // only flagged as stale (see useAuthLogic) so that no screen fires the request on its own; it is
  // requested again here, in the new language, the first time the bell is opened afterwards.
  useEffect(() => {
    if (isNotificationPopoverVisible && isStale) {
      dispatch(getPendingNotifications({}));
    }
  }, [isNotificationPopoverVisible, isStale, dispatch]);
  const notificationPopoverCont = useMemo(
    () => <NotificationContent>{notificationPopoverSwiper}</NotificationContent>,
    [notificationPopoverSwiper],
  );

  const notificationSwiper = (
    <Swiper
      className="notification-popover-slider"
      slidesPerView={'auto'}
      modules={[Navigation, Pagination]}
      navigation={{
        prevEl: '.prev-notif',
        nextEl: '.next-notif',
      }}
      pagination={{
        el: '.swiper-page',
        type: 'fraction',
        renderFraction: function (currentClass, totalClass) {
          return (
            '<span class="' +
            currentClass +
            '"></span>' +
            '<span>/</span>' +
            '<span class="' +
            totalClass +
            '"></span>'
          );
        },
      }}
    >
      <SwiperSlide
        className="notification-body"
        onClick={() => setIsNotificationPopoverVisible(false)}
      >
        {notificationList?.length > 0 ? (
          <>
            {notificationList.map((item, index) => (
              <NavLink to={item.urls} key={index + item?.text} className="nav-link">
                <div dangerouslySetInnerHTML={{ __html: item?.text }} />
                {notificationList?.length - 1 !== index && <div className="border" />}
              </NavLink>
            ))}
          </>
        ) : (
          <div>
            <CustomImage
              style={{ padding: '0px 10px 5px 0', height: '25px' }}
              src={iconBilgiYellow}
            />
            <Text t="youHaveNoNotifications" />
          </div>
        )}
      </SwiperSlide>
    </Swiper>
  );

  useEffect(() => {
    setNotificationPopoverSwiper(notificationSwiper);
    setNotificationPopoverContent(notificationPopoverCont);
  }, [isNotificationPopoverVisible, notificationList]);

  return (
    <CustomPopover
      placement="bottomRight"
      title={false}
      content={notificationPopoverContent}
      trigger="click"
      visible={isNotificationPopoverVisible}
      arrowPointAtCenter={true}
      overlayClassName="notification-popover"
      onVisibleChange={(value) => {
        setIsNotificationPopoverVisible(value);
      }}
    >
      {children}
    </CustomPopover>
  );
};

export default NotificationPopover;

NotificationPopover.propTypes = {
  children: PropTypes.node,
  isNotificationPopoverVisible: PropTypes.bool.isRequired,
  setIsNotificationPopoverVisible: PropTypes.func.isRequired,
  notificationList: PropTypes.array,
};
